import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BETA_FEATURE_CATALOG,
  type BetaFeatureKey,
  type SuperAdminBetaFeatureItem,
} from "@multi-chat/shared";
import { FlaskConical } from "lucide-react";
import {
  listSuperAdminBetaFeatures,
  updateSuperAdminBetaFeature,
} from "../../api/super-admin-api.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import { Switch } from "../../components/ui/switch/index.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";

type PendingToggle = {
  featureKey: BetaFeatureKey;
  nextEnabled: boolean;
};

export function SuperAdminBetaFeaturesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const [state, setState] = useState<SuperAdminLoadState<SuperAdminBetaFeatureItem[]>>({
    status: "loading",
  });
  const [attempt, setAttempt] = useState(0);
  const [pending, setPending] = useState<PendingToggle | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    listSuperAdminBetaFeatures(controller.signal).then(
      (response) => {
        if (!controller.signal.aborted) {
          setState({ status: "ready", data: response.items });
        }
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [attempt]);

  async function confirmToggle(): Promise<void> {
    if (!pending || state.status !== "ready") return;
    setSaving(true);
    try {
      const updated = await updateSuperAdminBetaFeature(pending.featureKey, pending.nextEnabled);
      setState({
        status: "ready",
        data: state.data.map((item) =>
          item.featureKey === updated.featureKey ? updated : item,
        ),
      });
      notifications?.pushNotification({
        message: t(
          pending.nextEnabled
            ? "superAdmin.betaFeatures.turnedOn"
            : "superAdmin.betaFeatures.turnedOff",
        ),
      });
      setPending(null);
    } catch {
      notifications?.pushNotification({ message: t("superAdmin.betaFeatures.saveError") });
    } finally {
      setSaving(false);
    }
  }

  if (state.status === "loading") {
    return (
      <div
        className="m-6 h-48 animate-pulse rounded-xl border border-line bg-surface"
        data-testid="super-admin-beta-features-loading"
        aria-busy="true"
      />
    );
  }

  if (state.status === "error") {
    return (
      <div className="p-6">
        <ProductErrorState
          testId="super-admin-beta-features-error"
          title={t("superAdmin.betaFeatures.loadErrorTitle")}
          description={t("superAdmin.betaFeatures.loadErrorBody")}
          onRetry={() => setAttempt((value) => value + 1)}
          retryLabel={t("superAdmin.retry")}
        />
      </div>
    );
  }

  const byKey = new Map(state.data.map((item) => [item.featureKey, item]));

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-5 p-6" data-testid="super-admin-beta-features">
      <header className="grid gap-1">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className="text-accent" aria-hidden="true" />
          <h1 className="m-0 text-lg font-semibold text-foreground">
            {t("superAdmin.betaFeatures.title")}
          </h1>
        </div>
        <p className="m-0 text-sm text-muted">{t("superAdmin.betaFeatures.subtitle")}</p>
      </header>

      {BETA_FEATURE_CATALOG.length === 0 ? (
        <div
          className="rounded-xl border border-line bg-surface p-6 text-sm text-muted"
          data-testid="super-admin-beta-features-empty"
        >
          {t("superAdmin.betaFeatures.empty")}
        </div>
      ) : (
        <ul className="m-0 grid list-none gap-3 p-0">
          {BETA_FEATURE_CATALOG.map((entry) => {
            const item = byKey.get(entry.key);
            const betaEnabled = item?.betaEnabled ?? true;
            const switchId = `super-admin-beta-feature-${entry.key}`;
            return (
              <li
                key={entry.key}
                className="grid gap-3 rounded-xl border border-line bg-surface p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                data-testid={`super-admin-beta-feature-${entry.key}`}
              >
                <div className="grid min-w-0 gap-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="m-0 text-sm font-semibold text-foreground">
                      {t(entry.nameKey)}
                    </h2>
                    <Badge variant={betaEnabled ? "warning" : "success"} className="px-1.5 py-0">
                      {betaEnabled
                        ? t("superAdmin.betaFeatures.betaOnBadge")
                        : t("superAdmin.betaFeatures.betaOffBadge")}
                    </Badge>
                    <Badge variant="outline" className="px-1.5 py-0 font-mono text-[0.6875rem]">
                      <LtrValue>{entry.key}</LtrValue>
                    </Badge>
                  </div>
                  <p className="m-0 text-xs leading-relaxed text-muted">{t(entry.descriptionKey)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-foreground" htmlFor={switchId}>
                    {betaEnabled
                      ? t("superAdmin.betaFeatures.betaOnShort")
                      : t("superAdmin.betaFeatures.betaOffShort")}
                  </label>
                  <Switch
                    id={switchId}
                    checked={betaEnabled}
                    data-testid={switchId}
                    aria-label={
                      betaEnabled
                        ? t("superAdmin.betaFeatures.betaOnShort")
                        : t("superAdmin.betaFeatures.betaOffShort")
                    }
                    onCheckedChange={(next) =>
                      setPending({ featureKey: entry.key, nextEnabled: next })
                    }
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>
            {pending?.nextEnabled
              ? t("superAdmin.betaFeatures.turnOnTitle")
              : t("superAdmin.betaFeatures.turnOffTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pending?.nextEnabled
              ? t("superAdmin.betaFeatures.turnOnBody")
              : t("superAdmin.betaFeatures.turnOffBody")}
          </AlertDialogDescription>
          <AlertDialogActions>
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setPending(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button type="button" disabled={saving} onClick={() => void confirmToggle()}>
              {saving ? t("superAdmin.betaFeatures.saving") : t("common.confirm")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
